/**
 * The live next step of the three planning screens that users most often leave on (24 Oct 2026): the credit
 * wall, "first page ready" and "design and architecture ready". Each says what is done, gives exactly one
 * step, and prices it against the live balance; while the balance cannot start it, Add Credits is the one
 * primary control and the plan's next credits are named. The wording matches the backend's
 * `next_step_copy` (ASD-STE100: short, active sentences).
 *
 * Pure: no runtime imports, so components and tests share it without the API client.
 */
import type { PhaseBuildExecutionState, PhaseBuildTaskOption } from '../services/buildPhaseService';
import type { DesignPagesResponse } from '../services/designService';

import { formatPhaseCredits } from './phaseBuildPresentation';

export type NextStepRowKey = 'cost' | 'finish' | 'balance' | 'grant' | 'live';

export interface NextStepRow {
  key: NextStepRowKey;
  label: string;
  value: string;
  /** The balance cannot start the step. */
  short?: boolean;
  href?: string;
}

export interface NextStepView {
  /** The live gate's text: a bold first sentence (what is done), then the one step. Null keeps the stored text. */
  message: string | null;
  rows: NextStepRow[];
  /** The one primary control: the step itself, or Add Credits while the balance cannot start it. */
  primary: 'step' | 'add_credits';
  /** Credits to add before the step can start; 0 when it can start now. */
  shortfall: number;
}

/** The plan's next automatic credits (`/billing/next-credit-grant`). */
export interface NextGrant {
  amount: number;
  scheduledAt: string;
  kind: 'free' | 'plan';
}

/** What `/planning-start-quote` says about one offered button. */
export interface StepQuote {
  balance: number;
  required: number;
  shortfall: number;
  openEnded: boolean;
  pages?: number;
  allPagesRequired?: number;
}

const EPSILON = 1e-9;

export const credits = (value: number): string => `${formatPhaseCredits(Math.round(value * 100) / 100)} ${
  Math.abs(value - 1) < EPSILON ? 'credit' : 'credits'}`;

const pageCount = (count: number): string => `${count} ${count === 1 ? 'page' : 'pages'}`;

/** "A", "A and B", "A, B and C", or "A, B, C, D, E and 2 more". */
export const nameList = (names: string[], limit = 5): string => {
  const shown = names.slice(0, limit);
  const rest = names.length - shown.length;

  if (rest > 0) return `${shown.join(', ')} and ${rest} more`;

  return shown.length <= 2 ? shown.join(' and ') : `${shown.slice(0, -1).join(', ')} and ${shown.at(-1)}`;
};

/** "in about 7 hours" / "in about 2 days" / "within the hour" / "now", like the chat's credit facts. */
export const waitWords = (minutes: number): string => {
  if (minutes <= 0) return 'now';
  if (minutes < 60) return 'within the hour';
  const hours = Math.round(minutes / 60);

  return hours < 48 ? `in about ${hours} ${hours === 1 ? 'hour' : 'hours'}`
    : `in about ${Math.round(minutes / 1440)} days`;
};

/** The "Free credits: +5 in about 7 hours" row, said only while the balance cannot start the step. */
export const grantRow = (
  grant: NextGrant | null | undefined, balance: number, need: number, now = Date.now(),
): NextStepRow | null => {
  const when = grant ? new Date(grant.scheduledAt).getTime() : Number.NaN;

  if (!grant || !(grant.amount > 0) || !Number.isFinite(when)) return null;
  const covers = balance + grant.amount + EPSILON >= need ? ' · enough for this step' : '';

  return {
    key: 'grant',
    label: grant.kind === 'free' ? 'Free credits' : 'Plan credits',
    value: `+${formatPhaseCredits(grant.amount)} ${waitWords(Math.ceil((when - now) / 60_000))}${covers}`,
  };
};

const balanceRow = (balance: number, shortfall: number): NextStepRow => ({
  key: 'balance',
  label: 'You have',
  value: shortfall > EPSILON ? `${credits(balance)} · ${credits(shortfall)} short` : credits(balance),
  short: shortfall > EPSILON,
});

/** Rows every screen shares: the balance, and the next grant while it is short. */
const balanceRows = (
  balance: number, shortfall: number, need: number, grant: NextGrant | null | undefined, now?: number,
): NextStepRow[] => {
  const next = shortfall > EPSILON ? grantRow(grant, balance, need, now) : null;

  return [balanceRow(balance, shortfall), ...(next ? [next] : [])];
};

/** While the balance cannot start the step, the text asks for credits first, as the one primary control does. */
export const withCreditsFirst = (message: string, view: Pick<NextStepView, 'primary'> | null | undefined): string => (
  view?.primary === 'add_credits' && !/\bAdd credits\b/.test(message)
    ? message.replace(/\bClick \*\*/, 'Add credits. Then click **') : message
);

const stepView = (message: string | null, rows: NextStepRow[], shortfall: number): NextStepView => ({
  message, rows, primary: shortfall > EPSILON ? 'add_credits' : 'step', shortfall: Math.max(0, shortfall),
});

// ── Credit wall ────────────────────────────────────────────────────────────────

export interface WaitingPage { name: string; credits: number }

export interface CreditWallFacts {
  done: number;
  total: number;
  /** In the order a resumed run designs them: cheapest first, pages with no price yet last. */
  waiting: WaitingPage[];
  balance: number;
  /** The cheapest waiting page: the next step. */
  next: number;
  /** Every waiting page. */
  finish: number;
}

const byPrice = (left: WaitingPage, right: WaitingPage): number => (
  (left.credits || Number.POSITIVE_INFINITY) - (right.credits || Number.POSITIVE_INFINITY)
);

const designedCount = (data: DesignPagesResponse): number => data.page_inventory?.ready.length
  ?? data.design_pages.filter((page) => !page.planned && !page.credit_blocked && !page.generation_failed
    && (page.has_compiled_html || Boolean(page.compiled_html))).length;

/** The live credit wall, read from the design-pages response; null while nothing waits for credits. */
export const creditWallFacts = (data: DesignPagesResponse | undefined): CreditWallFacts | null => {
  const status = data?.credit_status;

  if (!data || !status?.blocked_pages?.length) return null;
  const prices = new Map(data.design_pages.map((page) => [page.page_name, page.credit_required ?? 0]));
  const waiting = status.blocked_pages.map((name) => ({ name, credits: prices.get(name) ?? 0 })).sort(byPrice);
  const done = designedCount(data);
  const next = status.next_step_credits ?? (waiting[0]?.credits || status.required_credits);

  return {
    done, total: Math.max(data.page_inventory?.planned.length ?? 0, done + waiting.length), waiting,
    balance: status.available_credits, next, finish: Math.max(status.required_credits, next),
  };
};

/** The waiting pages the balance designs now, cheapest first, as the resumed run picks them. */
export const affordablePages = (waiting: WaitingPage[], balance: number): WaitingPage[] => waiting.reduce(
  (picked: { pages: WaitingPage[]; left: number }, page) => (
    page.credits > 0 && page.credits <= picked.left + EPSILON
      ? { pages: [...picked.pages, page], left: picked.left - page.credits } : picked
  ),
  { pages: [], left: balance },
).pages;

const wallHead = (facts: CreditWallFacts): string => {
  const names = facts.waiting.map((page) => page.name);

  if (facts.done <= 0 && names.length <= 1) return `**Your ${names[0] ?? 'first'} page waits for credits.**`;
  const verb = facts.done === 1 ? 'is' : 'are';
  const waits = names.length === 1 ? `1 page waits for credits: ${names[0]}.`
    : `${names.length} pages wait for credits: ${nameList(names)}.`;

  return `**${facts.done} of ${pageCount(facts.total)} ${verb} designed.** ${waits}`;
};

const wallStep = (facts: CreditWallFacts, now: WaitingPage[], press: string): string => {
  const all = facts.waiting.length;
  const target = all === 1 ? 'it' : 'them';

  if (!now.length) return `Add credits. Then click **${press}** to design ${target}.`;
  if (now.length === all) return `Click **${press}** to design ${target}.`;

  return `Click **${press}** to design ${nameList(now.map((page) => page.name))} now.`;
};

const wallCostRows = (facts: CreditWallFacts, now: WaitingPage[]): NextStepRow[] => {
  const nowCost = now.reduce((total, page) => total + page.credits, 0);
  const cost: NextStepRow = now.length
    ? { key: 'cost', label: 'Cost', value: `${credits(nowCost)} for ${now.length === 1 ? now[0].name : pageCount(now.length)}` }
    : { key: 'cost', label: 'Next page', value: `${credits(facts.next)} for ${facts.waiting[0]?.name ?? 'the next page'}` };
  const shown = now.length ? nowCost : facts.next;

  return facts.waiting.length > 1 && facts.finish > shown + EPSILON
    ? [cost, { key: 'finish', label: 'All pages', value: `${credits(facts.finish)} for ${pageCount(facts.waiting.length)}` }]
    : [cost];
};

/** Pages done, the pages that wait, and the one press: as many pages as the balance allows, else Add Credits. */
export const creditWallStep = (
  facts: CreditWallFacts, press: string, grant?: NextGrant | null, now?: number,
): NextStepView => {
  const designable = affordablePages(facts.waiting, facts.balance);
  const shortfall = designable.length ? 0 : Math.max(0, facts.next - facts.balance);
  const message = `${wallHead(facts)} ${wallStep(facts, designable, press)}`;

  return stepView(message, [
    ...wallCostRows(facts, designable), ...balanceRows(facts.balance, shortfall, facts.next, grant, now),
  ], shortfall);
};

// ── First page ready ───────────────────────────────────────────────────────────

const READY_PAGE = /Your \*{0,2}(.+?)\*{0,2} page design/;
const ELAPSED = /(\d+:\d{2})/;

/** The other pages "Generate All Pages" designs: no design yet, or waiting for credits. */
export const pagesStillToDesign = (data: DesignPagesResponse | undefined): string[] | null => {
  const inventory = data?.page_inventory;

  return inventory ? [...inventory.missing, ...inventory.credit_blocked] : null;
};

const firstPageMessage = (stored: string, remaining: string[], press: string): string | null => {
  const page = READY_PAGE.exec(stored)?.[1]?.trim();

  if (!page || !remaining.length) return null;
  const took = ELAPSED.exec(stored)?.[1];
  const others = remaining.length === 1 ? `the other page: ${remaining[0]}`
    : `the other ${remaining.length} pages: ${nameList(remaining)}`;

  return `**Your ${page} page design is ready.**${took ? ` It took ${took}.` : ''} Click **${press}** to design ${others}.`;
};

const allPagesCost = (quote: StepQuote, count: number, label = 'Cost'): NextStepRow => {
  const all = quote.allPagesRequired && quote.pages ? quote.allPagesRequired : null;
  const value = all !== null
    ? `${quote.openEnded ? 'about ' : ''}${credits(all)} for ${pageCount(quote.pages ?? count)}`
    : `${quote.openEnded ? 'at least ' : ''}${credits(quote.required)} for the next page`;

  return { key: label === 'Cost' ? 'cost' : 'finish', label, value };
};

/** Short of even the first page: price that page first, so the shortfall below reads against it. */
const firstPageCostRows = (quote: StepQuote, count: number): NextStepRow[] => (
  quote.shortfall > EPSILON && quote.allPagesRequired && quote.pages
    ? [
      { key: 'cost', label: 'Next page', value: `${quote.openEnded ? 'about ' : ''}${credits(quote.required)}` },
      allPagesCost(quote, count, 'All pages'),
    ]
    : [allPagesCost(quote, count)]
);

/** Pages the balance covers now when it starts the step but not every page ("about": unplanned pages vary). */
const partialPages = (quote: StepQuote): NextStepRow | null => {
  const total = quote.allPagesRequired ?? 0;
  const pages = quote.pages ?? 0;

  if (quote.shortfall > EPSILON || pages < 2 || quote.balance + EPSILON >= total) return null;
  const covered = Math.min(pages - 1, Math.max(1, Math.floor((quote.balance * pages) / total)));

  return {
    key: 'finish', label: 'Now', value: `about ${pageCount(covered)}; the rest wait for credits`,
  };
};

/** "Generate All Pages": the pages it designs, their price against the balance, and the live page slot. */
export const firstPageStep = (
  stored: string, remaining: string[], press: string, quote: StepQuote,
  grant?: NextGrant | null, liveUrl?: string | null, now?: number,
): NextStepView => {
  const partial = partialPages(quote);
  const live: NextStepRow[] = liveUrl ? [{ key: 'live', label: 'Live page', value: liveUrl, href: liveUrl }] : [];

  return stepView(firstPageMessage(stored, remaining, press), [
    ...firstPageCostRows(quote, remaining.length), ...(partial ? [partial] : []),
    ...balanceRows(quote.balance, quote.shortfall, quote.required, grant, now), ...live,
  ], quote.shortfall);
};

// ── Design and architecture ready: build the current phase ────────────────────

export interface BuildStepFacts {
  state: PhaseBuildExecutionState;
  /** The explicit task selection, when the picker owns the build scope. */
  selectedTaskIds?: string[];
  /** What the press is short of: the selection's gap, or the phase's when nothing can start. */
  shortfall: number;
}

const OPEN_TASK = (task: PhaseBuildTaskOption): boolean => task.selectable !== false
  && !['done', 'completed'].includes(task.status);

const selectedTasks = ({ state, selectedTaskIds }: BuildStepFacts): PhaseBuildTaskOption[] => {
  const tasks = state.runnable_tasks ?? [];

  if (selectedTaskIds) return tasks.filter((task) => selectedTaskIds.includes(task.id));
  const defaults = tasks.filter((task) => task.selected_by_default !== false);

  // Nothing the balance can start: the phase's open tasks are what its price covers.
  return defaults.length ? defaults : (state.phase_tasks ?? []).filter(OPEN_TASK);
};

const taskCount = (count: number): string => `${count} ${count === 1 ? 'task' : 'tasks'}`;

const buildCostRow = (facts: BuildStepFacts, tasks: PhaseBuildTaskOption[]): NextStepRow => {
  const cost = facts.selectedTaskIds
    ? tasks.reduce((total, task) => total + (task.estimated_credits || 0), 0) : facts.state.required_credits;

  return { key: 'cost', label: 'Cost', value: `${credits(cost)} for ${taskCount(tasks.length)}` };
};

const startsNow = (starting: number, waiting: number): string => `${starting} ${
  starting === 1 ? 'task starts' : 'tasks start'} now; ${waiting} ${waiting === 1 ? 'waits' : 'wait'} in Backlog`;

const partialBuildRow = ({ state, selectedTaskIds, shortfall }: BuildStepFacts): NextStepRow | null => (
  !selectedTaskIds && shortfall <= EPSILON && state.credit_shortfall > EPSILON && state.can_start
    ? { key: 'finish', label: 'Now', value: startsNow(state.affordable_task_count ?? 0, state.deferred_task_count ?? 0) }
    : null
);

/** Nothing can start: the cheapest open task is the smallest build, so it is priced first. */
const cheapestTask = (facts: BuildStepFacts, tasks: PhaseBuildTaskOption[]): PhaseBuildTaskOption | null => (
  !facts.selectedTaskIds && facts.shortfall > EPSILON
    ? tasks.filter((task) => task.estimated_credits > 0)
      .reduce<PhaseBuildTaskOption | null>((low, task) => (
        !low || task.estimated_credits < low.estimated_credits ? task : low), null)
    : null
);

const buildPriceRows = (
  facts: BuildStepFacts, tasks: PhaseBuildTaskOption[], next: PhaseBuildTaskOption | null,
): NextStepRow[] => {
  const cost = buildCostRow(facts, tasks);
  const partial = partialBuildRow(facts);

  if (next) {
    return [
      { key: 'cost', label: 'Next task', value: `${credits(next.estimated_credits)} for ${next.title}` },
      { ...cost, key: 'finish', label: 'All tasks' },
    ];
  }

  return partial ? [cost, partial] : [cost];
};

/** "Start Building Phase N": the selected tasks' price against the balance; Add Credits when nothing can start. */
export const buildStep = (facts: BuildStepFacts, grant?: NextGrant | null, now?: number): NextStepView | null => {
  const { state } = facts;

  if (!state.current_phase || state.all_completed || state.is_unlimited) return null;
  // A running build, or tasks to repair first: the press is not "build these tasks", so there is no price to show.
  if (state.blocking_reason && state.blocking_reason !== 'insufficient_credits') return null;
  const tasks = selectedTasks(facts);
  const next = cheapestTask(facts, tasks);
  const shortfall = next ? Math.max(0, next.estimated_credits - state.available_credits) : facts.shortfall;

  return stepView(null, [
    ...buildPriceRows(facts, tasks, next),
    ...balanceRows(state.available_credits, shortfall, shortfall + state.available_credits, grant, now),
  ], shortfall || facts.shortfall);
};
